import { createContext, useContext, useState, useMemo, useEffect, useRef } from "react";
import { useUser, useAuth as useClerkAuth } from "@clerk/clerk-react";
import { setAuthTokenGetter } from "../services/api";

const AuthContext = createContext(null);

function resolveRole(clerkUser) {
  // Role from Clerk publicMetadata (set in Clerk dashboard per user)
  const metaRole = clerkUser?.publicMetadata?.role;
  if (metaRole && ["admin", "exec", "sales", "it-admin", "unassigned"].includes(metaRole)) {
    return metaRole;
  }
  return "unassigned"; // no access until admin assigns a role
}

export function AuthProvider({ children }) {
  const { isLoaded, isSignedIn, user: clerkUser } = useUser();
  const { getToken } = useClerkAuth();
  const [roleOverride, setRoleOverride] = useState(null);

  // Wire up Clerk token getter so api.js can attach Bearer token to requests
  useEffect(() => {
    if (getToken) setAuthTokenGetter(getToken);
  }, [getToken]);

  const role = resolveRole(clerkUser);

  // Auto-claim role from invitation when user is unassigned
  const claimAttempted = useRef(false);
  useEffect(() => {
    if (!isSignedIn || !clerkUser || role !== "unassigned" || claimAttempted.current) return;
    claimAttempted.current = true;
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch("/api/claim-role", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "x-clerk-token": token },
        });
        if (res.ok) {
          // Role was applied server-side — reload Clerk user to pick it up
          await clerkUser.reload();
        }
      } catch {
        // Claim failed — fall through to polling
      }
    })();
  }, [isSignedIn, clerkUser, role, getToken]);

  // Fallback: poll Clerk for metadata changes (e.g. admin manually assigns role)
  useEffect(() => {
    if (!isSignedIn || !clerkUser || role !== "unassigned") return;
    const interval = setInterval(() => {
      clerkUser.reload();
    }, 5000);
    return () => clearInterval(interval);
  }, [isSignedIn, clerkUser, role]);

  // Expose a user object that matches the shape the rest of the app expects
  const user = isSignedIn
    ? {
        userDetails: clerkUser.primaryEmailAddress?.emailAddress || clerkUser.fullName || "User",
        userId: clerkUser.id,
        userRoles: [role],
        identityProvider: "clerk",
      }
    : null;

  const value = useMemo(
    () => ({
      user,
      role: roleOverride || role,
      realRole: role,
      setRoleOverride,
      loading: !isLoaded,
      getToken, // expose so api.js can attach Bearer token
    }),
    [user, role, roleOverride, isLoaded, getToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
